// Debounced validation of referral + promo codes against the
// `selfservice-validate-codes` edge function. Used by the review step to
// drive "Referred by …" / "10% promo applied" pills and price math.
//
// Each field validates independently — editing the promo input doesn't
// re-fire the referral check, and vice versa. Empty inputs short-circuit
// to state='empty' so the hook is cheap to call.

import React from 'react';
import { ApiError, apiClient } from '../../lib/apiClient';
import * as analytics from '../../lib/analytics';
import { EVENTS } from '../../lib/analytics';

export type RefState =
  | { state: 'empty' }
  | { state: 'pending' }
  | { state: 'valid'; name: string; defaultDiscountPercent: number | null }
  | { state: 'invalid' };

export type PromoState =
  | { state: 'empty' }
  | { state: 'pending' }
  | { state: 'valid'; discountPercent: number }
  | { state: 'invalid'; reason?: 'expired' | 'exhausted' | 'already_used' | 'inactive' | 'not_found' | 'rate_limited' };

export interface CodeValidation {
  ref: RefState;
  promo: PromoState;
}

const DEBOUNCE_MS = 400;

export function useCodeValidation(
  slug: string | undefined,
  refCode: string,
  promoCode: string,
  contactEmail: string | undefined,
): CodeValidation {
  const [refResult, setRefResult] = React.useState<RefState>(
    refCode ? { state: 'pending' } : { state: 'empty' },
  );
  const [promoResult, setPromoResult] = React.useState<PromoState>(
    promoCode ? { state: 'pending' } : { state: 'empty' },
  );

  const refTrim = refCode.trim();
  const promoTrim = promoCode.trim();
  const emailTrim = contactEmail?.trim() || undefined;

  // Referral validation. Doesn't depend on contactEmail — referrals don't
  // have once-per-buyer logic.
  React.useEffect(() => {
    if (!slug) return;
    if (!refTrim) {
      setRefResult({ state: 'empty' });
      return;
    }
    setRefResult({ state: 'pending' });
    const cancel = { aborted: false };
    const timer = window.setTimeout(async () => {
      try {
        const data = await apiClient.post<{ ref?: { valid: boolean; name?: string; defaultDiscountPercent?: number | null } }>(
          '/codes/validate',
          { slug, refCode: refTrim },
        );
        if (cancel.aborted) return;
        if (!data.ref?.valid) {
          // CTV-733: debounced, so this counts settled invalid attempts (a
          // pause mid-typing can fire), not distinct buyers. Never the code
          // text itself.
          analytics.track(EVENTS.CODE_VALIDATION_FAILED, { station_slug: slug, code_type: 'referral' });
        }
        setRefResult(
          data.ref?.valid
            ? { state: 'valid', name: data.ref.name ?? '', defaultDiscountPercent: data.ref.defaultDiscountPercent ?? null }
            : { state: 'invalid' },
        );
      } catch (e) {
        if (cancel.aborted) return;
        // A rate-limited check must not tell the buyer their code is bad;
        // fall back to the empty (no-pill) state silently.
        if (e instanceof ApiError && e.status === 429) {
          setRefResult({ state: 'empty' });
          return;
        }
        setRefResult({ state: 'invalid' });
      }
    }, DEBOUNCE_MS);
    return () => { cancel.aborted = true; window.clearTimeout(timer); };
  }, [slug, refTrim]);

  // Promo validation. contactEmail matters here for the once-per-buyer cap.
  React.useEffect(() => {
    if (!slug) return;
    if (!promoTrim) {
      setPromoResult({ state: 'empty' });
      return;
    }
    setPromoResult({ state: 'pending' });
    const cancel = { aborted: false };
    const timer = window.setTimeout(async () => {
      try {
        const data = await apiClient.post<{
          promo?: { valid: boolean; discountPercent?: number; reason?: 'expired' | 'exhausted' | 'already_used' | 'inactive' | 'not_found' };
        }>('/codes/validate', { slug, promoCode: promoTrim, contactEmail: emailTrim });
        if (cancel.aborted) return;
        if (!data.promo?.valid) {
          // CTV-733: reason distinguishes marketing problems (expired /
          // exhausted campaigns) from typos (not_found). Never the code text.
          analytics.track(EVENTS.CODE_VALIDATION_FAILED, {
            station_slug: slug,
            code_type: 'promo',
            reason: data.promo?.reason ?? 'not_found',
          });
        }
        setPromoResult(
          data.promo?.valid
            ? { state: 'valid', discountPercent: data.promo.discountPercent ?? 0 }
            : { state: 'invalid', reason: data.promo?.reason },
        );
      } catch (e) {
        if (cancel.aborted) return;
        // Distinguish "slow down" from "bad code" so the buyer isn't told a
        // valid promo is unrecognized while rate limited.
        if (e instanceof ApiError && e.status === 429) {
          setPromoResult({ state: 'invalid', reason: 'rate_limited' });
          return;
        }
        setPromoResult({ state: 'invalid' });
      }
    }, DEBOUNCE_MS);
    return () => { cancel.aborted = true; window.clearTimeout(timer); };
  }, [slug, promoTrim, emailTrim]);

  return React.useMemo(() => ({ ref: refResult, promo: promoResult }), [refResult, promoResult]);
}
